import {ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, OnDestroy, Output, Inject} from '@angular/core';
import { eroiService } from '../../services/eroi-metric.service';
import { MixpanelService } from "../../../../core/services/mixpanel.service";
import {custTile, tvdData, TvdValuePillar} from "../../types/tvd-types";
import { TotalValueDeliveryComponent } from '../../total-value-delivery.component';
import { TVDService } from 'src/app/features/total-value-delivery/services/tvd.service';
import { Subscription } from 'rxjs';
import { FiltersService } from 'src/app/core/services/filters.service';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { CurrencyUomService } from 'src/app/core/services/currency-uom.service';
import { UIPreferencesService } from 'src/app/core/services/ui-preferences.service';

@Component({
    selector: 'custom-tile-item',
    templateUrl: './custom-tile-item.component.html',
    styleUrls: ['./custom-tile-item.component.scss', '../../styles/tvd-repeat-styles.scss'],
    standalone: false
})
export class CustomTileItemComponent implements OnInit, OnChanges, OnDestroy {
  filters: ContractModel | undefined;
  private filtersSubscription: Subscription | undefined;
  private currencySubscription: Subscription | undefined;
  private displayInRoundedNumberSubscription: Subscription | undefined;
  private disableSelectionButtonSubscription: Subscription | undefined;

  pngPath: string | undefined;
  custTileTitle: string | undefined;
  custTileValue: string | undefined;
  custTileCost: string | undefined;
  custTileUnit: string | undefined;
  custTileValuePillar: string | undefined;
  custTileImpactCategory : string | undefined;

  costCurrencyCode: string = "USD";
  displayInRoundedNumber: boolean = true;

  custTileSubscription: Subscription | undefined;

  // Disable the select button for adding to the presentation
  // if the limit of selected metrics per value pillar is reached and this.selectedItem==false
  disableSelectionButton: boolean = false;

  @Input() id: string | undefined;
  @Input() makingSelectionsState: boolean | undefined;
  @Input() selectionMapping: Map<string, [data:tvdData | undefined, isSelected:boolean, isAddedToPres:boolean]> | undefined;
  @Input() customTilesDict: Map<string, custTile> | undefined;
  @Input() selectedItem : boolean | undefined = false;
  @Input() addedToPres : boolean | undefined = false;

  @Output() selected = new EventEmitter<void>();
  @Output() unselected = new EventEmitter<void>();
  
  constructor(
      private mixpanelService: MixpanelService,
      private tvdComponent: TotalValueDeliveryComponent,
      private tvdService: TVDService,
      private filtersService: FiltersService,
      private eroiMetricService: eroiService,
      private currencyUomService: CurrencyUomService,
      private uiPreferencesService: UIPreferencesService,
      private cdr: ChangeDetectorRef
  ) { }

  iconPlusHover : boolean = false;
  data : tvdData | undefined ;

  ngOnInit(): void {
    this.filtersSubscription = this.filtersService.selectedFiltersObservable.subscribe(f => this.filters = f);
    this.displayInRoundedNumberSubscription = this.uiPreferencesService.displayRoundedValuesObservable.subscribe(f => this.displayInRoundedNumber = f);

    if(this.filters)
      this.costCurrencyCode = this.currencyUomService.getDisplayedCurrency();

    this.custTileSubscription = this.tvdService.customTilesObservable.subscribe((dict) => {
      this.customTilesDict = dict;
      if(this.id && this.customTilesDict){
        let values = this.customTilesDict.get(this.id);
        if(values){
          this.custTileTitle = values.title;
          this.custTileValue = values.metricValue.toString();
          this.custTileCost = values.cost;
          this.custTileUnit = values.unit;
          this.custTileValuePillar = values.valuePillar;
          this.custTileImpactCategory = values.impactCategory;
          this.pngPath = this.eroiMetricService.mapImpactCategoryToIconPath(this.custTileImpactCategory);
          this.cdr.detectChanges();
        }
      }
    });

    if(this.selectionMapping && this.custTileTitle && this.custTileValue && this.custTileCost && this.custTileValuePillar && this.custTileImpactCategory){
      this.selectedItem = this.selectionMapping?.get(this.custTileTitle)?.[1];

      //if the tile hasnt been set in the selectionMapping, do it now      
      if(this.selectedItem===undefined){
        this.selectionMapping.set(this.custTileTitle, [{monetaryContribution:this.custTileCost, valueContribution:this.custTileValue}, false, false]);
      }
    }

    //Add self to object tracker
    this.tvdService.custTileObjectList?.push(this);

    this.currencySubscription = this.currencyUomService.displayedCurrency$.subscribe(() => {
      this.costCurrencyCode = this.currencyUomService.getDisplayedCurrency();
    });

    // subscribe to when the value pillar metric selection limit is reached
    this.disableSelectionButtonSubscription = this.tvdService.selectedMetricsLimitObservable.subscribe(limitObj => {
      this.updateDisableSelectionButton();
    });
  }

  ngOnChanges(){
    if(this.custTileTitle!=undefined){

      this.selectedItem=this.selectionMapping?.get(this.custTileTitle)?.[1];
      this.addedToPres=this.selectionMapping?.get(this.custTileTitle)?.[2];

      this.updateDisableSelectionButton();     
    }
  }

  ngOnDestroy(): void {
    this.filtersSubscription?.unsubscribe();
    this.currencySubscription?.unsubscribe();
    this.displayInRoundedNumberSubscription?.unsubscribe();
    this.custTileSubscription?.unsubscribe();
    this.disableSelectionButtonSubscription?.unsubscribe();
  }

  toggleSelectItem(title: string | undefined){
    //If the button is disabled, do nothing on click
    if(!this.disableSelectionButton){
      this.selectedItem = !this.selectedItem;

      // Update selectionMapping before emitting so parent pillar-state checks read current state.
      if(this.custTileTitle!==undefined && this.selectionMapping!==undefined){
        let tempArray = this.selectionMapping.get(this.custTileTitle);
        if(tempArray!=undefined){
          this.selectionMapping?.set(this.custTileTitle, [tempArray[0], this.selectedItem, tempArray[2]]);
        }
      }

      if(this.selectedItem) {
        this.trackPlusSignClick(title);
        this.selected.emit();
      } else {
        this.trackMinusSignClick(title);
        this.unselected.emit();
      }
    } 
  }


  trackPlusSignClick(title: string|undefined) {
    this.mixpanelService.track('TVD ' + title + ' Added', {});
  }

  trackMinusSignClick(title: string|undefined) {
    this.mixpanelService.track('TVD ' + title + ' Removed', {});
  }

  editCustomTile(){
    this.tvdComponent.custTileEditVisible = true;
    
    //Set values for when we choose to edit
    this.tvdService.currCustCost = this.custTileCost;
    this.tvdService.currCustTitle = this.custTileTitle;
    this.tvdService.currCustUnit = this.custTileUnit;
    this.tvdService.currCustValue = this.custTileValue;
    this.tvdService.currCustValuePillar = this.custTileValuePillar;
    this.tvdService.currCustImpactCategory = this.custTileImpactCategory;
  }

  /**
   * Disable the select button for adding to the presentation
   * if the limit of selected metrics per value pillar is reached and this.selectedItem==false
   */
  private updateDisableSelectionButton(){
    let valuePillarForMetric = this.custTileValuePillar
    if(valuePillarForMetric) {
      let valuePillarLimitReached = this.tvdService.getSelectedMetricsLimit(valuePillarForMetric as TvdValuePillar);
      this.disableSelectionButton = this.selectedItem == false && valuePillarLimitReached == true;
    }    
  }

}
